import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Trophy } from "lucide-react";
import StuckButton from "@/components/StuckButton";
import VoicePractice from "@/components/VoicePractice";
import ReflectionCard from "@/components/ReflectionCard";

export default function Lesson() {
  const { lessonId } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/curriculum/lessons/${lessonId}`);
      setLesson(data);
      if (user?.role === "student") {
        api.post("/progress", { lesson_id: lessonId, status: "in_progress" }).catch(() => {});
      }
    })();
  }, [lessonId, user?.role]);

  if (!lesson) return <div className="text-slate-500">Loading…</div>;

  const score = lesson.quiz.length
    ? Math.round((lesson.quiz.filter((q, i) => answers[i] === q.answer_idx).length / lesson.quiz.length) * 100)
    : 100;

  const submit = async () => {
    setShowResults(true);
    if (user?.role !== "student") return;
    try {
      const r = await api.post("/progress", { lesson_id: lessonId, status: "completed", score });
      setCompleted(true);
      toast.success(`Lesson complete! +${r.data.points_awarded} points`);
      refresh();
    } catch { toast.error("Could not save progress"); }
  };

  const beach = user?.beach_location;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid={`lesson-page-${lessonId}`}>
      <Link to={`/subjects/${lesson.subject}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back to subject</Link>

      <div>
        <div className="chip mb-2">{lesson.module}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{lesson.title}</h1>
        <p className="mt-2 text-slate-600">{lesson.summary}</p>
      </div>

      {/* Video */}
      {(lesson.video_url) && (
        <div className="space-y-2" data-testid="lesson-video">
          <div className="aspect-video w-full rounded-3xl overflow-hidden border border-slate-200 bg-slate-100">
            <iframe src={lesson.video_url} title={lesson.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin" />
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
            <span>Video not playing?</span>
            <a data-testid="lesson-video-open"
              href={lesson.video_url.replace("/embed/", "/watch?v=")}
              target="_blank" rel="noreferrer"
              className="text-teal-700 font-semibold hover:underline">Open on YouTube</a>
            <span>·</span>
            <a data-testid="lesson-video-search"
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(lesson.title + " " + lesson.subject)}`}
              target="_blank" rel="noreferrer"
              className="text-teal-700 font-semibold hover:underline">Search YouTube for this topic</a>
          </div>
        </div>
      )}

      {/* Objectives */}
      <section className="p-6 rounded-3xl bg-white border border-slate-200">
        <h3 className="font-display text-lg font-semibold mb-2">What you'll do</h3>
        <ul className="space-y-1.5 text-slate-700">
          {lesson.objectives.map((o, i) => (
            <li key={i} className="flex gap-2"><span className="text-teal-700 mt-1">•</span> {o}</li>
          ))}
        </ul>
      </section>

      {/* Beach lab callout */}
      {lesson.module === "Beach Lab" && (
        <div className="p-6 rounded-3xl bg-cyan-50 border border-cyan-200" data-testid="beach-lab-callout">
          <div className="chip mb-2" style={{background:"hsl(190 70% 92%)",color:"hsl(190 80% 22%)",borderColor:"hsl(190 50% 82%)"}}>Beach Lab</div>
          <div className="font-display text-lg font-semibold text-slate-900">Your beach: {beach || "Set your beach in Profile"}</div>
          <p className="text-sm text-slate-700 mt-1">Take safety precautions. Bring a notebook and your phone camera for observations.</p>
        </div>
      )}

      {/* Practice */}
      <section className="p-6 rounded-3xl bg-white border border-slate-200">
        <h3 className="font-display text-lg font-semibold mb-2">Guided practice</h3>
        <ol className="list-decimal ml-5 space-y-2 text-slate-700">
          {lesson.practice.map((p, i) => <li key={i}>{p}</li>)}
        </ol>
      </section>

      {/* Voice practice (Spanish) */}
      {lesson.subject === "spanish" && lesson.speak_targets && (
        <VoicePractice targets={lesson.speak_targets} />
      )}

      {/* Quiz */}
      {lesson.quiz.length > 0 && (
        <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-4" data-testid="lesson-quiz">
          <h3 className="font-display text-lg font-semibold">Quick check</h3>
          {lesson.quiz.map((q, i) => (
            <div key={i}>
              <div className="font-medium text-slate-900">{i+1}. {q.q}</div>
              <div className="mt-2 grid gap-2">
                {q.choices.map((c, j) => {
                  const selected = answers[i] === j;
                  const isCorrect = showResults && j === q.answer_idx;
                  const isWrong = showResults && selected && j !== q.answer_idx;
                  return (
                    <button key={j} data-testid={`quiz-${i}-${j}`}
                      onClick={() => setAnswers({ ...answers, [i]: j })}
                      disabled={showResults}
                      className={`text-left px-4 py-3 rounded-xl border transition-colors
                        ${isCorrect ? "border-teal-500 bg-teal-50 text-teal-900" :
                          isWrong ? "border-red-300 bg-red-50 text-red-800" :
                          selected ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-white hover:border-teal-200"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}

      {!completed ? (
        <button data-testid="lesson-submit" onClick={submit}
          disabled={lesson.quiz.length > 0 && Object.keys(answers).length < lesson.quiz.length}
          className="w-full py-4 rounded-full bg-teal-700 hover:bg-teal-800 disabled:bg-slate-300 text-white font-semibold transition-colors">
          {lesson.quiz.length > 0 ? "Submit & complete" : "Mark complete"}
        </button>
      ) : (
        <div className="p-6 rounded-3xl bg-teal-50 border border-teal-200 text-center" data-testid="lesson-complete">
          <CheckCircle2 className="mx-auto text-teal-700" size={40} />
          <div className="mt-2 font-display text-xl font-bold text-slate-900">Nice work — {score}% on the check.</div>
          <div className="mt-4 flex justify-center gap-2">
            <button data-testid="lesson-next-back" onClick={() => nav(`/subjects/${lesson.subject}`)} className="px-5 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold">Back to subject</button>
            <button onClick={() => nav("/student")} className="px-5 py-2 rounded-full border border-slate-300 text-sm font-semibold">Today's missions</button>
          </div>
        </div>
      )}

      {/* Reflection: shown after completion, but also before if student wants to jot notes */}
      {user?.role === "student" && (completed || lesson.quiz.length === 0) && (
        <ReflectionCard lessonId={lesson.id} />
      )}

      {user?.role === "student" && <StuckButton defaultSubject={lesson.subject} defaultTopic={lesson.title} />}
    </div>
  );
}
